import { useEffect, useState } from "react";
import api from "../services/api";

function Dashboard() {
  const [currencyReport, setCurrencyReport] = useState({
    AFN: { 
      income: 0, 
      expenses: 0, 
      withdrawals: 0, 
      payments: 0,
      balance: 0
    },
    USD: { 
      income: 0, 
      expenses: 0, 
      withdrawals: 0, 
      payments: 0,
      balance: 0,
      exchangeRate: 66
    }
  });
  const [totalInAFN, setTotalInAFN] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState({
    startDate: '',
    endDate: ''
  });

  // Financial Summary States
  const [financialSummary, setFinancialSummary] = useState({
    totalIncome: { AFN: 0, USD: 0 },
    totalExpenses: { AFN: 0, USD: 0 },
    netProfit: { AFN: 0, USD: 0 }
  });

  // Project Summary State
  const [projectSummary, setProjectSummary] = useState({
    AFN: { budget: 0, paid: 0, remaining: 0 },
    USD: { budget: 0, paid: 0, remaining: 0 },
    totalProjects: 0
  });

  // Employee Spending State
  const [employeeSpending, setEmployeeSpending] = useState([]);

  const getCurrencySymbol = (currencyCode) => {
    const symbols = {
      'USD': '$',
      'AFN': '؋'
    };
    return symbols[currencyCode] || currencyCode;
  };

  const formatNumber = (number) => {
    return Math.round(number).toLocaleString();
  };

  // Filter data by date range
  const filterByDate = (data, dateField = 'date') => {
    if (!dateFilter.startDate && !dateFilter.endDate) return data;

    return data.filter(item => {
      const itemDate = new Date(item[dateField] || item.created_at);
      const start = dateFilter.startDate ? new Date(dateFilter.startDate) : null;
      const end = dateFilter.endDate ? new Date(dateFilter.endDate) : null;

      if (start && itemDate < start) return false;
      if (end && itemDate > end) return false;
      return true;
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [incomesRes, expensesRes, withdrawalsRes, paymentsRes, projectsRes, employeesRes] = await Promise.all([
          api.get("incomes/"),
          api.get("expenses/"),
          api.get("withdrawals/"),
          api.get("payments/"),
          api.get("projects/"),
          api.get("employees/")
        ]);

        let incomes = incomesRes.data;
        let expenses = expensesRes.data;
        let withdrawals = withdrawalsRes.data;
        let payments = paymentsRes.data;
        let projects = projectsRes.data;
        let employees = employeesRes.data;

        // Apply date filtering
        if (dateFilter.startDate || dateFilter.endDate) {
          incomes = filterByDate(incomes, 'date');
          expenses = filterByDate(expenses, 'date');
          withdrawals = filterByDate(withdrawals, 'date');
          payments = filterByDate(payments, 'created_at');
          projects = filterByDate(projects, 'created_at');
        }

        // Initialize report with AFN values
        const report = {
          AFN: { income: 0, expenses: 0, withdrawals: 0, payments: 0, balance: 0, exchangeRate: 1 },
          USD: { income: 0, expenses: 0, withdrawals: 0, payments: 0, balance: 0, exchangeRate: 66 }
        };

        // Initialize financial summary
        const financials = {
          totalIncome: { AFN: 0, USD: 0 },
          totalExpenses: { AFN: 0, USD: 0 },
          netProfit: { AFN: 0, USD: 0 }
        };

        // Initialize project summary
        const projectSum = {
          AFN: { budget: 0, paid: 0, remaining: 0 },
          USD: { budget: 0, paid: 0, remaining: 0 },
          totalProjects: projects.length
        };

        let latestUSDRate = 66;

        // Process regular incomes (from Income table)
        incomes.forEach(income => {
          const currency = income.currency || 'AFN';
          const amount = parseFloat(income.amount || 0);
          const exchangeRate = parseFloat(income.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
          
          // Convert to AFN using exchange rate
          const amountInAFN = amount * exchangeRate;
          report[currency].income += amount;
          financials.totalIncome[currency] += amount;
          
          if (currency === 'USD' && income.exchange_rate) {
            latestUSDRate = exchangeRate;
          }
        });

        // Process project payments (from Payment table) - This is the only project income we count
        payments.forEach(payment => {
          const currency = payment.currency || 'USD';
          const amount = parseFloat(payment.amount || 0);
          const exchangeRate = parseFloat(payment.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
          
          // Convert to AFN using exchange rate
          const amountInAFN = amount * exchangeRate;
          report[currency].payments += amount;
          financials.totalIncome[currency] += amount;
          
          if (currency === 'USD' && payment.exchange_rate) {
            latestUSDRate = exchangeRate;
          }
        });

        // Process regular expenses
        expenses.forEach(expense => {
          const currency = expense.currency || 'AFN';
          const amount = parseFloat(expense.amount || 0);
          const exchangeRate = parseFloat(expense.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
          
          // Convert to AFN using exchange rate
          const amountInAFN = amount * exchangeRate;
          report[currency].expenses += amount;
          financials.totalExpenses[currency] += amount;
          
          if (currency === 'USD' && expense.exchange_rate) {
            latestUSDRate = exchangeRate;
          }
        });

        // Process withdrawals
        withdrawals.forEach(withdrawal => {
          const currency = withdrawal.currency || 'AFN';
          const amount = parseFloat(withdrawal.amount || 0);
          const exchangeRate = parseFloat(withdrawal.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
          
          // Convert to AFN using exchange rate
          const amountInAFN = amount * exchangeRate;
          report[currency].withdrawals += amount;
          financials.totalExpenses[currency] += amount;
          
          if (currency === 'USD' && withdrawal.exchange_rate) {
            latestUSDRate = exchangeRate;
          }
        });

        // Process projects for summary only (NOT for income calculation)
        if (Array.isArray(projects)) {
          projects.forEach(project => {
            const currency = project.currency || 'USD';
            const budget = parseFloat(project.budget || 0);
            const paid = parseFloat(project.total_paid || 0);
            const remaining = budget - paid;
            const exchangeRate = parseFloat(project.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
            
            // Add to project summary
            projectSum[currency].budget += budget;
            projectSum[currency].paid += paid;
            projectSum[currency].remaining += remaining;
            
            if (currency === 'USD' && project.exchange_rate) {
              latestUSDRate = exchangeRate;
            }
          });
        }

        // Calculate employee spending
        const employeeSpendingData = employees.map(employee => {
          const employeeExpenses = expenses.filter(exp => exp.employee === employee.id);
          const totalSpent = employeeExpenses.reduce((sum, exp) => {
            const amount = parseFloat(exp.amount || 0);
            const currency = exp.currency || 'AFN';
            const exchangeRate = parseFloat(exp.exchange_rate || (currency === 'USD' ? latestUSDRate : 1));
            
            // Convert to AFN for consistent reporting
            return sum + (amount * exchangeRate);
          }, 0);

          return {
            id: employee.id,
            name: employee.name,
            role: employee.role,
            expenseCount: employeeExpenses.length,
            totalSpent: totalSpent
          };
        }).filter(emp => emp.expenseCount > 0) // Only show employees with expenses
          .sort((a, b) => b.totalSpent - a.totalSpent); // Sort by highest spending

        setEmployeeSpending(employeeSpendingData);

        // Update exchange rates
        report.USD.exchangeRate = latestUSDRate;

        // Calculate balances for each currency (in original currency)
        Object.keys(report).forEach(currency => {
          const data = report[currency];
          data.balance = (data.income + data.payments) - (data.expenses + data.withdrawals);
        });

        // Calculate financial summary
        financials.netProfit.AFN = financials.totalIncome.AFN - financials.totalExpenses.AFN;
        financials.netProfit.USD = financials.totalIncome.USD - financials.totalExpenses.USD;

        // Calculate total in AFN by converting ALL currencies to AFN
        let totalAFN = 0;
        
        // AFN amounts (already in AFN, no conversion needed)
        totalAFN += report.AFN.balance;
        
        // USD amounts - convert to AFN using exchange rate
        totalAFN += report.USD.balance * report.USD.exchangeRate;

        setCurrencyReport(report);
        setTotalInAFN(totalAFN);
        setFinancialSummary(financials);
        setProjectSummary(projectSum);
      } catch (error) {
        console.error("API Error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [dateFilter]);

  const handleDateFilterChange = (e) => {
    const { name, value } = e.target;
    setDateFilter(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearDateFilter = () => {
    setDateFilter({
      startDate: '',
      endDate: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  // Calculate USD value in AFN
  const usdBalanceInAFN = currencyReport.USD.balance * currencyReport.USD.exchangeRate;

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2 sm:gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
            Currency Wealth Report
          </h1>
          <p className="text-gray-600 mt-1 text-xs sm:text-base">Complete overview of your money in AFN and USD</p>
        </div>
        <div className="text-xs sm:text-sm text-gray-500 bg-gray-100 px-2 sm:px-3 py-1 sm:py-2 rounded-lg mt-2 sm:mt-0">
          Exchange Rate: 1 USD = {currencyReport.USD.exchangeRate} AFN
        </div>
      </div>

      {/* Date Filter - Minimal on Mobile */}
      <div className="sm:bg-white sm:border sm:border-gray-200 sm:rounded-xl sm:shadow-sm sm:p-4">
        {/* Mobile: Simple two rows without card styling */}
        <div className="sm:hidden space-y-2">
          <div className="flex space-x-2">
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={dateFilter.startDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={dateFilter.endDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex space-x-2">
            <button
              onClick={clearDateFilter}
              className="flex-1 px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-xs"
            >
              Clear Filter
            </button>
          </div>
          {(dateFilter.startDate || dateFilter.endDate) && (
            <div className="text-xs text-gray-600 text-center">
              {dateFilter.startDate || 'Beginning'} to {dateFilter.endDate || 'Now'}
            </div>
          )}
        </div>

        {/* Desktop: Card styling */}
        <div className="hidden sm:block">
          <h2 className="text-sm sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Date Range</h2>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-4 items-end">
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Start Date
              </label>
              <input
                type="date"
                name="startDate"
                value={dateFilter.startDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs sm:text-base border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                End Date
              </label>
              <input
                type="date"
                name="endDate"
                value={dateFilter.endDate}
                onChange={handleDateFilterChange}
                className="w-full px-2 py-1.5 text-xs sm:text-base border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div className="w-full sm:w-auto mt-1">
              <button
                onClick={clearDateFilter}
                className="w-full sm:w-auto px-3 py-1.5 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors text-xs"
              >
                Clear Filter
              </button>
            </div>
          </div>
          {(dateFilter.startDate || dateFilter.endDate) && (
            <div className="mt-1 text-xs text-gray-600">
              {dateFilter.startDate || 'Beginning'} to {dateFilter.endDate || 'Now'}
            </div>
          )}
        </div>
      </div>

      {/* Total Wealth Summary */}
      <div className="bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl sm:rounded-2xl p-4 sm:p-6 text-white">
        <div className="flex flex-col lg:flex-row justify-between items-center">
          <div className="text-center lg:text-left mb-3 sm:mb-4 lg:mb-0">
            <h2 className="text-sm sm:text-lg font-semibold opacity-90">TOTAL WEALTH IN AFGHANI</h2>
            <p className="text-xl sm:text-3xl lg:text-4xl font-bold mt-1 sm:mt-2">
              ؋{formatNumber(totalInAFN)}
            </p>
            <p className="text-blue-100 mt-1 sm:mt-2 text-xs sm:text-sm">All currencies converted to AFN using exchange rates</p>
          </div>
          <div className="flex space-x-4 sm:space-x-6">
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold">${formatNumber(currencyReport.USD.balance)}</div>
              <div className="text-blue-100 text-xs sm:text-sm">US Dollar</div>
              <div className="text-blue-200 text-xs">= ؋{formatNumber(usdBalanceInAFN)}</div>
            </div>
            <div className="text-center">
              <div className="text-lg sm:text-2xl font-bold">؋{formatNumber(currencyReport.AFN.balance)}</div>
              <div className="text-blue-100 text-xs sm:text-sm">Afghan Afghani</div>
            </div>
          </div>
        </div>
      </div>

      {/* Employee Spending Summary */}
      <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-orange-200 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-3 sm:p-4 text-white">
          <div className="flex justify-between items-center">
            <h3 className="text-sm sm:text-lg font-bold">Employee Spending</h3>
            <div className="text-xl sm:text-2xl">👤</div>
          </div>
          <p className="text-orange-100 text-xs sm:text-sm">
            Total spent by employees {dateFilter.startDate || dateFilter.endDate ? '(filtered)' : ''}
          </p>
        </div>
        <div className="p-4 sm:p-6">
          {employeeSpending.length === 0 ? (
            <div className="text-center py-4">
              <div className="text-gray-400 text-lg mb-2">💸</div>
              <p className="text-gray-500 text-sm">No employee expenses found</p>
              <p className="text-gray-400 text-xs mt-1">
                {dateFilter.startDate || dateFilter.endDate ? 'Try changing date filter' : 'Employees will appear here when they make expenses'}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {employeeSpending.map((employee, index) => (
                <div 
                  key={employee.id} 
                  className="flex justify-between items-center p-3 bg-orange-50 rounded-lg border border-orange-200 hover:bg-orange-100 transition-colors duration-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="flex-shrink-0 w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {index + 1}
                    </div>
                    <div>
                      <div className="font-medium text-orange-800 text-sm">{employee.name}</div>
                      <div className="text-orange-600 text-xs capitalize">{employee.role}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-bold text-orange-600">
                      ؋{formatNumber(employee.totalSpent)}
                    </div>
                    <div className="text-orange-500 text-xs">
                      {employee.expenseCount} expense{employee.expenseCount !== 1 ? 's' : ''}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Total Summary */}
              <div className="mt-4 pt-3 border-t border-orange-200">
                <div className="flex justify-between items-center">
                  <span className="font-semibold text-orange-700 text-sm">Total Employee Spending:</span>
                  <span className="text-lg font-bold text-orange-600">
                    ؋{formatNumber(employeeSpending.reduce((sum, emp) => sum + emp.totalSpent, 0))}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Financial Summary - Both Currencies */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* AFN Financial Summary */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-green-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">AFN - Financial Summary</h3>
              <div className="text-xl sm:text-2xl">؋</div>
            </div>
            <p className="text-green-100 text-xs sm:text-sm">All amounts in Afghan Afghani</p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <div className="text-green-700 font-medium text-sm sm:text-base">Total Income</div>
                  <div className="text-green-600 text-xs sm:text-sm">Regular + Project Payments</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    ؋{formatNumber(financialSummary.totalIncome.AFN)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <div className="text-red-700 font-medium text-sm sm:text-base">Total Expenses</div>
                  <div className="text-red-600 text-xs sm:text-sm">Expenses + Withdrawals</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-red-600">
                    ؋{formatNumber(financialSummary.totalExpenses.AFN)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <div className="text-blue-700 font-medium text-sm sm:text-base">Net Profit</div>
                  <div className="text-blue-600 text-xs sm:text-sm">Income - Expenses</div>
                </div>
                <div className="text-right">
                  <div className={`text-lg sm:text-xl font-bold ${
                    financialSummary.netProfit.AFN >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ؋{formatNumber(financialSummary.netProfit.AFN)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* USD Financial Summary */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-blue-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">USD - Financial Summary</h3>
              <div className="text-xl sm:text-2xl">$</div>
            </div>
            <div className="flex justify-between items-center text-blue-100 text-xs sm:text-sm">
              <span>All amounts in US Dollar</span>
              <span className="font-bold">1 USD = {currencyReport.USD.exchangeRate} AFN</span>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <div className="text-green-700 font-medium text-sm sm:text-base">Total Income</div>
                  <div className="text-green-600 text-xs sm:text-sm">Regular + Project Payments</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    ${formatNumber(financialSummary.totalIncome.USD)}
                  </div>
                  <div className="text-green-500 text-xs sm:text-sm">
                    = ؋{formatNumber(financialSummary.totalIncome.USD * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-red-50 rounded-lg border border-red-200">
                <div>
                  <div className="text-red-700 font-medium text-sm sm:text-base">Total Expenses</div>
                  <div className="text-red-600 text-xs sm:text-sm">Expenses + Withdrawals</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-red-600">
                    ${formatNumber(financialSummary.totalExpenses.USD)}
                  </div>
                  <div className="text-red-500 text-xs sm:text-sm">
                    = ؋{formatNumber(financialSummary.totalExpenses.USD * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <div className="text-blue-700 font-medium text-sm sm:text-base">Net Profit</div>
                  <div className="text-blue-600 text-xs sm:text-sm">Income - Expenses</div>
                </div>
                <div className="text-right">
                  <div className={`text-lg sm:text-xl font-bold ${
                    financialSummary.netProfit.USD >= 0 ? 'text-green-600' : 'text-red-600'
                  }`}>
                    ${formatNumber(financialSummary.netProfit.USD)}
                  </div>
                  <div className={`text-xs sm:text-sm ${
                    financialSummary.netProfit.USD >= 0 ? 'text-green-500' : 'text-red-500'
                  }`}>
                    = ؋{formatNumber(financialSummary.netProfit.USD * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Project Summary */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* AFN Project Summary */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-purple-200 overflow-hidden">
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">Projects - AFN</h3>
              <div className="text-xl sm:text-2xl">؋</div>
            </div>
            <p className="text-purple-100 text-xs sm:text-sm">Total: {projectSummary.totalProjects} projects</p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-center p-3 sm:p-4 bg-purple-50 rounded-lg border border-purple-200">
                <div>
                  <div className="text-purple-700 font-medium text-sm sm:text-base">Total Budget</div>
                  <div className="text-purple-600 text-xs sm:text-sm">All AFN Projects</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-purple-600">
                    ؋{formatNumber(projectSummary.AFN.budget)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <div className="text-green-700 font-medium text-sm sm:text-base">Total Paid</div>
                  <div className="text-green-600 text-xs sm:text-sm">Payments Received</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    ؋{formatNumber(projectSummary.AFN.paid)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <div className="text-blue-700 font-medium text-sm sm:text-base">Total Remaining</div>
                  <div className="text-blue-600 text-xs sm:text-sm">Budget - Paid</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-blue-600">
                    ؋{formatNumber(projectSummary.AFN.remaining)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* USD Project Summary */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-indigo-200 overflow-hidden">
          <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">Projects - USD</h3>
              <div className="text-xl sm:text-2xl">$</div>
            </div>
            <div className="flex justify-between items-center text-indigo-100 text-xs sm:text-sm">
              <span>Total: {projectSummary.totalProjects} projects</span>
              <span className="font-bold">1 USD = {currencyReport.USD.exchangeRate} AFN</span>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-center p-3 sm:p-4 bg-indigo-50 rounded-lg border border-indigo-200">
                <div>
                  <div className="text-indigo-700 font-medium text-sm sm:text-base">Total Budget</div>
                  <div className="text-indigo-600 text-xs sm:text-sm">All USD Projects</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-indigo-600">
                    ${formatNumber(projectSummary.USD.budget)}
                  </div>
                  <div className="text-indigo-500 text-xs sm:text-sm">
                    = ؋{formatNumber(projectSummary.USD.budget * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                <div>
                  <div className="text-green-700 font-medium text-sm sm:text-base">Total Paid</div>
                  <div className="text-green-600 text-xs sm:text-sm">Payments Received</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-green-600">
                    ${formatNumber(projectSummary.USD.paid)}
                  </div>
                  <div className="text-green-500 text-xs sm:text-sm">
                    = ؋{formatNumber(projectSummary.USD.paid * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                <div>
                  <div className="text-blue-700 font-medium text-sm sm:text-base">Total Remaining</div>
                  <div className="text-blue-600 text-xs sm:text-sm">Budget - Paid</div>
                </div>
                <div className="text-right">
                  <div className="text-lg sm:text-xl font-bold text-blue-600">
                    ${formatNumber(projectSummary.USD.remaining)}
                  </div>
                  <div className="text-blue-500 text-xs sm:text-sm">
                    = ؋{formatNumber(projectSummary.USD.remaining * currencyReport.USD.exchangeRate)}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Currency Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* AFN Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-green-200 overflow-hidden">
          <div className="bg-gradient-to-r from-green-500 to-green-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">Afghan Afghani</h3>
              <div className="text-xl sm:text-2xl">؋</div>
            </div>
            <p className="text-green-100 text-xs sm:text-sm">Exchange Rate: 1 AFN = 1 AFN</p>
          </div>
          <div className="p-4 sm:p-6">
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-xl sm:text-3xl font-bold text-gray-800">
                ؋{formatNumber(currencyReport.AFN.balance)}
              </div>
              <div className={`text-xs sm:text-sm font-medium ${currencyReport.AFN.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                Current Balance
              </div>
            </div>
            
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center p-2 sm:p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium text-xs sm:text-sm">Regular Income:</span>
                <span className="text-green-600 font-bold text-xs sm:text-sm">؋{formatNumber(currencyReport.AFN.income)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium text-xs sm:text-sm">Project Payments:</span>
                <span className="text-green-600 font-bold text-xs sm:text-sm">؋{formatNumber(currencyReport.AFN.payments)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-red-50 rounded-lg">
                <span className="text-red-700 font-medium text-xs sm:text-sm">Regular Expenses:</span>
                <span className="text-red-600 font-bold text-xs sm:text-sm">؋{formatNumber(currencyReport.AFN.expenses)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-red-50 rounded-lg">
                <span className="text-red-700 font-medium text-xs sm:text-sm">Withdrawals:</span>
                <span className="text-red-600 font-bold text-xs sm:text-sm">؋{formatNumber(currencyReport.AFN.withdrawals)}</span>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700 text-sm sm:text-base">Net Balance:</span>
                <span className={`text-base sm:text-lg font-bold ${currencyReport.AFN.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ؋{formatNumber(currencyReport.AFN.balance)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* USD Card */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-lg border border-blue-200 overflow-hidden">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 p-3 sm:p-4 text-white">
            <div className="flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-bold">US Dollar</h3>
              <div className="text-xl sm:text-2xl">$</div>
            </div>
            <div className="flex justify-between items-center text-blue-100 text-xs sm:text-sm">
              <span>Current Exchange Rate</span>
              <span className="font-bold">1 USD = {currencyReport.USD.exchangeRate} AFN</span>
            </div>
          </div>
          <div className="p-4 sm:p-6">
            <div className="text-center mb-4 sm:mb-6">
              <div className="text-xl sm:text-3xl font-bold text-gray-800">
                ${formatNumber(currencyReport.USD.balance)}
              </div>
              <div className="text-sm sm:text-lg text-gray-600">
                = ؋{formatNumber(usdBalanceInAFN)}
              </div>
              <div className={`text-xs sm:text-sm font-medium ${currencyReport.USD.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                Current Balance in USD
              </div>
            </div>
            
            <div className="space-y-2 sm:space-y-3">
              <div className="flex justify-between items-center p-2 sm:p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium text-xs sm:text-sm">Regular Income:</span>
                <span className="text-green-600 font-bold text-xs sm:text-sm">${formatNumber(currencyReport.USD.income)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-green-50 rounded-lg">
                <span className="text-green-700 font-medium text-xs sm:text-sm">Project Payments:</span>
                <span className="text-green-600 font-bold text-xs sm:text-sm">${formatNumber(currencyReport.USD.payments)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-red-50 rounded-lg">
                <span className="text-red-700 font-medium text-xs sm:text-sm">Regular Expenses:</span>
                <span className="text-red-600 font-bold text-xs sm:text-sm">${formatNumber(currencyReport.USD.expenses)}</span>
              </div>
              <div className="flex justify-between items-center p-2 sm:p-3 bg-red-50 rounded-lg">
                <span className="text-red-700 font-medium text-xs sm:text-sm">Withdrawals:</span>
                <span className="text-red-600 font-bold text-xs sm:text-sm">${formatNumber(currencyReport.USD.withdrawals)}</span>
              </div>
            </div>

            <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex justify-between items-center">
                <span className="font-semibold text-gray-700 text-sm sm:text-base">Net Balance in USD:</span>
                <span className={`text-base sm:text-lg font-bold ${currencyReport.USD.balance >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  ${formatNumber(currencyReport.USD.balance)}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1 sm:mt-2">
                <span className="font-semibold text-gray-700 text-sm sm:text-base">Value in AFN:</span>
                <span className="text-base sm:text-lg font-bold text-blue-600">
                  ؋{formatNumber(usdBalanceInAFN)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Wealth Breakdown */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Total Wealth Calculation */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Total Wealth Calculation</h2>
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg sm:rounded-xl border border-green-200">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base mr-2 sm:mr-3">
                  ؋
                </div>
                <div>
                  <div className="font-semibold text-green-700 text-sm sm:text-base">Afghani Cash Balance</div>
                  <div className="text-green-600 text-xs sm:text-sm">Direct AFN holdings</div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-base sm:text-xl font-bold text-green-600">
                  ؋{formatNumber(currencyReport.AFN.balance)}
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center p-3 sm:p-4 bg-blue-50 rounded-lg sm:rounded-xl border border-blue-200">
              <div className="flex items-center">
                <div className="w-8 h-8 sm:w-10 sm:h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold text-sm sm:text-base mr-2 sm:mr-3">
                  $
                </div>
                <div>
                  <div className="font-semibold text-blue-700 text-sm sm:text-base">Dollar Value in AFN</div>
                  <div className="text-blue-600 text-xs sm:text-sm">
                    ${formatNumber(currencyReport.USD.balance)} × {currencyReport.USD.exchangeRate} AFN
                  </div>
                </div>
              </div>
              <div className="text-right">
                <div className="text-base sm:text-xl font-bold text-blue-600">
                  ؋{formatNumber(usdBalanceInAFN)}
                </div>
                <div className="text-blue-500 text-xs sm:text-sm">
                  (${formatNumber(currencyReport.USD.balance)})
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center p-3 sm:p-4 bg-gradient-to-r from-gray-800 to-gray-600 rounded-lg sm:rounded-xl text-white">
              <div>
                <div className="font-semibold text-sm sm:text-base">TOTAL WEALTH IN AFN</div>
                <div className="text-gray-300 text-xs sm:text-sm">AFN Balance + USD Converted to AFN</div>
              </div>
              <div className="text-right">
                <div className="text-xl sm:text-2xl font-bold">؋{formatNumber(totalInAFN)}</div>
                <div className="text-gray-300 text-xs sm:text-sm">
                  = {formatNumber(currencyReport.AFN.balance)} + {formatNumber(usdBalanceInAFN)}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Summary & Insights */}
        <div className="bg-white rounded-xl sm:rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-6">
          <h2 className="text-lg sm:text-xl font-bold text-gray-900 mb-4 sm:mb-6">Financial Summary</h2>
          <div className="space-y-4 sm:space-y-6">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              <div className="text-center p-3 sm:p-4 bg-green-50 rounded-lg sm:rounded-xl border border-green-200">
                <div className="text-lg sm:text-2xl font-bold text-green-600">
                  ؋{formatNumber(currencyReport.AFN.balance)}
                </div>
                <div className="text-gray-600 text-xs sm:text-sm mt-1">Afghani Cash</div>
              </div>
              <div className="text-center p-3 sm:p-4 bg-blue-50 rounded-lg sm:rounded-xl border border-blue-200">
                <div className="text-lg sm:text-2xl font-bold text-blue-600">
                  ${formatNumber(currencyReport.USD.balance)}
                </div>
                <div className="text-gray-600 text-xs sm:text-sm mt-1">Dollar Cash</div>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center">
                <div className="text-yellow-600 text-base sm:text-lg mr-2 sm:mr-3">💰</div>
                <div>
                  <div className="font-semibold text-yellow-800 text-sm sm:text-base">Wealth Breakdown</div>
                  <div className="text-yellow-700 text-xs sm:text-sm mt-1">
                    You have <strong>${formatNumber(currencyReport.USD.balance)}</strong> in US Dollars and <strong>؋{formatNumber(currencyReport.AFN.balance)}</strong> in Afghanis.
                    Combined total: <strong>؋{formatNumber(totalInAFN)}</strong>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-semibold text-blue-800 text-sm sm:text-base">Exchange Rate</div>
                  <div className="text-blue-700 text-xs sm:text-sm mt-1">
                    1 US Dollar = {currencyReport.USD.exchangeRate} Afghanis
                  </div>
                </div>
                <div className="text-xl sm:text-2xl text-blue-600">💱</div>
              </div>
            </div>

            {/* Quick Calculation */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl p-3 sm:p-4">
              <div className="text-xs sm:text-sm font-semibold text-gray-700 mb-2">Quick Calculation:</div>
              <div className="text-xs text-gray-600 space-y-1">
                <div>AFN Balance: ؋{formatNumber(currencyReport.AFN.balance)}</div>
                <div>USD Balance: ${formatNumber(currencyReport.USD.balance)}</div>
                <div>USD to AFN: ${formatNumber(currencyReport.USD.balance)} × {currencyReport.USD.exchangeRate} = ؋{formatNumber(usdBalanceInAFN)}</div>
                <div className="font-bold text-gray-800 text-xs sm:text-sm">Total: ؋{formatNumber(currencyReport.AFN.balance)} + ؋{formatNumber(usdBalanceInAFN)} = ؋{formatNumber(totalInAFN)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;